import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../../dashboard.style.js';
import { Firestore } from 'firebase/firestore';
import { getPreventiveMaintenanceByCompany, getPMWorkOrdersByCompany, getAssetsByCompany, getUsersByCompany } from '@cmms/api';
import { WorkOrderStatusCat } from '@cmms/models';

@customElement('pm-mini-dashboard')
export class PMMiniDashboard extends LitElement {
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  private upcoming: Array<any> = [];
  private overdue: Array<any> = [];
  private completedCount = 0;
  private users: Record<string, string> = {};
  private assetName: Record<string, string> = {};
  private pmAnalyticsSummary: {
    total: number;
    completed: number;
    upcoming: number;
    overdue: number;
    avgCompletionHrs: number;
    onTime: number;
    late: number;
    complianceRate: number;
  } | null = null;
  @state() private linkedSummary: { linked: number; unlinked: number; totalPMTemplates: number; totalPMWorkOrders: number } | null = null;
  @state() private invalidRefs: { invalidUserRefs: number; invalidAssetRefs: number } | null = null;

  static styles = [
    styles,
    css`
      .mini-dashboard { display:flex; flex-direction:column; gap:1.5rem; background: var(--sl-color-neutral-50); padding:1rem; }
      .mini-header { display:flex; align-items:center; gap:1rem; }
      .bar-chart { width:100%; background: var(--sl-color-neutral-200); height: 12px; border-radius: 999px; overflow: hidden; }
      .bar-fill { height: 100%; background: var(--sl-color-success-500); }
      .kpi-row { display:grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap:1.5rem; }
      .data-table { width:100%; border-collapse: collapse; }
      .data-table th, .data-table td { padding: 0.75rem; text-align:left; border-bottom:1px solid var(--sl-color-neutral-200); }
      .data-table th { color: var(--sl-color-neutral-600); font-weight:600; font-size:0.875rem; }
      .data-table td { color: var(--sl-color-neutral-900); font-size:0.875rem; }
      .analytics-grid { display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 1.5rem; }
      @media (max-width: 900px) { .analytics-grid { grid-template-columns: 1fr; } }
      .metric-card { background:white; border-radius:1rem; box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.06); padding:1rem; display:flex; flex-direction:column; gap:0.25rem; }
      .metric-title { color: var(--sl-color-neutral-600); font-size:0.85rem; }
      .metric-value { color: var(--sl-color-neutral-900); font-weight:600; font-size:1.25rem; }
      .metric-subtle { color: var(--sl-color-neutral-600); font-size:0.8rem; }
    `
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId')) this.loadData();
  }

  private async loadData() {
    if (!this.companyId || !this.firestore) return;
    try {
      const [pmResp, pmWoResp, assetsResp, usersResp] = await Promise.all([
        getPreventiveMaintenanceByCompany(this.firestore, this.companyId),
        getPMWorkOrdersByCompany(this.firestore, this.companyId),
        getAssetsByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId)
      ]);

      const pmList: any[] = Array.isArray((pmResp as any).data) ? (pmResp as any).data : (Array.isArray(pmResp) ? (pmResp as any) : []);
      const pmWOs: any[] = Array.isArray((pmWoResp as any).data) ? (pmWoResp as any).data : (Array.isArray(pmWoResp) ? (pmWoResp as any) : []);
      const assets: any[] = Array.isArray((assetsResp as any).data) ? (assetsResp as any).data : (Array.isArray(assetsResp) ? (assetsResp as any) : []);
      const rawUsers: any[] = Array.isArray((usersResp as any).data) ? (usersResp as any).data : (Array.isArray(usersResp) ? (usersResp as any) : []);

      // Build indices
      this.assetName = {};
      for (const a of assets) {
        const m = a.model || {};
        const id = a.id;
        if (id) this.assetName[id] = m.name || a.name || 'Asset';
      }
      this.users = {};
      for (const u of rawUsers) {
        const id = u.id;
        const m = u.model || {};
        this.users[id] = m.full_name || m.name || u.name || 'User';
      }

      // Developer-only diagnostics: PM–WO Live Link Check
      try {
        const linkedSummary = (pmWOs || []).reduce((acc: { linked: number; unlinked: number }, wo: any) => {
          const pmId = wo.model?.pm_template_id || (wo as any).pm_template_id;
          if (pmId) acc.linked++;
          else acc.unlinked++;
          return acc;
        }, { linked: 0, unlinked: 0 });

        this.linkedSummary = { ...linkedSummary, totalPMTemplates: (pmList || []).length, totalPMWorkOrders: (pmWOs || []).length };
      } catch (e) {
        // Silent fail
      }

      // Temporary diagnostics: PM Reference Integrity Check
      try {
        const userMap = new Map(Object.entries(this.users));
        const assetMap = new Map(Object.entries(this.assetName));
        const pmRecords = (pmList || []).map(pm => ({
          id: pm.id,
          assigned_to: pm.model?.default_assigned_to || pm.model?.assigned_to || '',
          asset_id: pm.model?.asset_id || ''
        }));

        const pmWithUser = pmRecords.filter(pm => !!pm.assigned_to);
        const invalidUserRefs = pmWithUser.filter(pm => !userMap.has(pm.assigned_to));

        const pmWithAssets = pmRecords.filter(pm => !!pm.asset_id);
        const invalidAssetRefs = pmWithAssets.filter(pm => !assetMap.has(pm.asset_id));

        this.invalidRefs = { invalidUserRefs: invalidUserRefs.length, invalidAssetRefs: invalidAssetRefs.length };
      } catch (e) {
        // Silent fail
      }

      // === PM Data Validation Report ===
      try {
        const userMap = new Map(Object.entries(this.users));
        const assetMap = new Map(Object.entries(this.assetName));

        const total = pmList.length;
        let missing_company_id = 0, missing_asset_id = 0, missing_assigned_to = 0;
        let missing_freq_unit = 0, missing_freq_value = 0, missing_next_due_at = 0;
        let missing_created = 0, missing_updated = 0, inactiveCount = 0;
        let invalidUserRefs = 0, invalidAssetRefs = 0;

        // Cross-check PM WOs linkage and completion consistency
        const pmWoByTemplate = new Map<string, any[]>();
        (pmWOs || []).forEach(wo => {
          const m = wo.model || {};
          const key = m.pm_template_id || null;
          if (!key) return;
          if (!pmWoByTemplate.has(key)) pmWoByTemplate.set(key, []);
          pmWoByTemplate.get(key)!.push(wo);
        });

        let pmWithCompletedWO = 0;
        let completedBeforeDue = 0;
        let completedAfterDue = 0;

        for (const rec of pmList) {
          const id = rec.id;
          const m = rec.model || {};
          const company_id = m.company_id;
          const asset_id = m.asset_id;
          const assigned_to = m.default_assigned_to; // PM template assigned user
          const freq_unit = m.freq_unit;
          const freq_value = m.freq_value;
          const next_due_at = m.next_due_at;
          const created_at = m.created_at;
          const updated_at = m.updated_at;
          const is_active = m.active !== false;

          if (!company_id) missing_company_id++;
          if (!asset_id) missing_asset_id++;
          if (!assigned_to) missing_assigned_to++;
          if (!freq_unit) missing_freq_unit++;
          if (!freq_value && freq_value !== 0) missing_freq_value++;
          if (!next_due_at) missing_next_due_at++;
          if (!created_at) missing_created++;
          if (!updated_at) missing_updated++;
          if (!is_active) inactiveCount++;

          if (asset_id && !assetMap.has(asset_id)) invalidAssetRefs++;
          if (assigned_to && !userMap.has(assigned_to)) invalidUserRefs++;

          // PM WO linkage and timing
          const wos = pmWoByTemplate.get(id) || [];
          const completedWOs = wos.filter(wo => (wo.model?.status_cat ?? wo.status_cat) === WorkOrderStatusCat.COMPLETED);
          if (completedWOs.length > 0) {
            pmWithCompletedWO++;
            const latest = completedWOs
              .map(wo => (wo.model?.completed_at ?? wo.model?.updated_at ?? wo.completed_at ?? wo.updated_at) as number)
              .filter(Boolean)
              .sort((a, b) => b - a)[0];
            const due = next_due_at as number | undefined;
            if (latest && due) {
              if (latest <= due) completedBeforeDue++; else completedAfterDue++;
            }
          }
        }

        // Frequency distributions
        const freqUnitCounts: Record<string, number> = {};
        const freqValueCounts: Record<string, number> = {};
        pmList.forEach(rec => {
          const m = rec.model || {};
          const fu = (m.freq_unit || '');
          const fv = (m.freq_value || '');
          if (fu) freqUnitCounts[fu] = (freqUnitCounts[fu] || 0) + 1;
          if (fv !== '') freqValueCounts[fv] = (freqValueCounts[fv] || 0) + 1;
        });

        // Derived field availability
        const pmDueDerivable = pmList.filter(rec => !!(rec.model?.next_due_at)).length;
      } catch (e) {
        // Silent fail
      }

      const now = Date.now();
      const startOfToday = new Date(); startOfToday.setHours(0,0,0,0);
      const startOfTodayMs = startOfToday.getTime();

      const isOverdue = (pm: any) => {
        const due = pm.model?.next_due_at;
        if (!due) return false;
        const graceDays = Number(pm.model?.grace_days || 0);
        const graceMs = graceDays * 86400000;
        return pm.model?.active !== false && (due + graceMs) < now;
      };

      const upcomingSorted = pmList
        .filter(pm => pm.model?.active && pm.model?.next_due_at && pm.model.next_due_at >= startOfTodayMs)
        .sort((a, b) => a.model.next_due_at - b.model.next_due_at);
      const overdueSorted = pmList
        .filter(pm => isOverdue(pm))
        .sort((a, b) => a.model.next_due_at - b.model.next_due_at);

      this.upcoming = upcomingSorted.slice(0, 5).map(pm => ({
        id: pm.id,
        asset: this.assetName[pm.model.asset_id] || 'Unknown Asset',
        due: pm.model.next_due_at,
        assigned_to: pm.model.default_assigned_to || ''
      }));
      this.overdue = overdueSorted.slice(0, 5).map(pm => ({
        id: pm.id,
        asset: this.assetName[pm.model.asset_id] || 'Unknown Asset',
        due: pm.model.next_due_at,
        assigned_to: pm.model.default_assigned_to || ''
      }));

      // Completed count from PM work orders (last 30 days)
      const thirtyDaysAgo = Date.now() - 30 * 86400000;
      this.completedCount = (pmWOs || []).filter(wo => {
        const m = wo.model || {};
        const status = m.status_cat ?? wo.status_cat;
        const completedAt = m.updated_at ?? m.created_at ?? wo.updated_at ?? 0;
        return status === WorkOrderStatusCat.COMPLETED && (completedAt || 0) >= thirtyDaysAgo;
      }).length;

      this.requestUpdate();

      // --- PM Analytics Diagnostics (read-only) ---
      try {
        const totalPMs = pmList.length;

        // Determine status for PMs from template data
        const todayStart = new Date(); todayStart.setHours(0,0,0,0);
        const todayStartMs = todayStart.getTime();

        const isPMOverdue = (pm: any) => {
          const due = pm.model?.next_due_at;
          if (!due) return false;
          const graceDays = Number(pm.model?.grace_days || 0);
          const graceMs = graceDays * 86400000;
          return pm.model?.active !== false && (due + graceMs) < Date.now();
        };

        const upcomingPMs = pmList.filter(pm => pm.model?.active && pm.model?.next_due_at && pm.model.next_due_at >= todayStartMs);
        const overduePMs = pmList.filter(pm => isPMOverdue(pm));

        // Completed PMs: infer from PM Work Orders by pm_template_id
        const completedPMWOs = (pmWOs || []).filter(wo => (wo.model?.status_cat ?? wo.status_cat) === WorkOrderStatusCat.COMPLETED);
        const completedByTemplate = new Set<string>();
        completedPMWOs.forEach(wo => {
          const tpl = wo.model?.pm_template_id ?? wo.pm_template_id;
          if (tpl) completedByTemplate.add(tpl);
        });
        const completedPMs = completedByTemplate.size;

        // Avg completion time (hrs) and on-time vs late (from PM WOs)
        const completedWithTimes = completedPMWOs
          .map(wo => ({
            completed_at: wo.model?.completed_at ?? wo.completed_at ?? null,
            pm_due_at: wo.model?.pm_due_at ?? wo.pm_due_at ?? null
          }))
          .filter(x => x.completed_at && x.pm_due_at) as Array<{ completed_at: number; pm_due_at: number }>;

        const durations = completedWithTimes.map(x => (x.completed_at - x.pm_due_at) / 3.6e6);
        const avgCompletionHrs = durations.length ? +(durations.reduce((a, b) => a + b, 0) / durations.length).toFixed(1) : 0;
        const onTime = completedWithTimes.filter(x => x.completed_at <= x.pm_due_at).length;
        const late = completedWithTimes.length - onTime;

        const complianceRate = totalPMs > 0 ? Math.round((completedPMs / totalPMs) * 100) : 0;

        if (completedPMs > 0) {
          this.pmAnalyticsSummary = {
            total: totalPMs,
            completed: completedPMs,
            upcoming: upcomingPMs.length,
            overdue: overduePMs.length,
            avgCompletionHrs,
            onTime,
            late,
            complianceRate
          };
        }
      } catch (e) {
        // Silent fail
      }
    } catch (err) {
      console.error('Failed to load PM data:', err);
      this.upcoming = [];
      this.overdue = [];
      this.completedCount = 0;
    }
  }

  private formatDate(ts?: number) { return ts ? new Date(ts).toLocaleDateString() : ''; }

  render() {
    const upcomingCount = this.upcoming.length;
    const overdueCount = this.overdue.length;
    const total = Math.max(1, upcomingCount + overdueCount + this.completedCount);
    const completionRate = Math.round((this.completedCount / total) * 100);
    const onTimePct = this.pmAnalyticsSummary ? Math.round((this.pmAnalyticsSummary.onTime / Math.max(1, this.pmAnalyticsSummary.onTime + this.pmAnalyticsSummary.late)) * 100) : 0;
    const avgHrs = this.pmAnalyticsSummary?.avgCompletionHrs ?? 0;
    const avgDisplay = avgHrs >= 48 ? `${(avgHrs/24).toFixed(1)} d` : `${avgHrs} h`;
    const linked = this.linkedSummary?.linked ?? 0;
    const linkedTotal = this.linkedSummary?.totalPMWorkOrders ?? 0;
    const allLinkedOk = linkedTotal > 0 && linked === linkedTotal;
    const integrityIssues = (this.invalidRefs?.invalidUserRefs ?? 0) + (this.invalidRefs?.invalidAssetRefs ?? 0);

    return html`
      <div class="mini-dashboard">
        <div class="mini-header" style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <h2 style="margin:0;font-size:1.25rem;">Preventive Maintenance Overview</h2>
        </div>

        <div class="overview-cards">
          <div class="overview-card"><h3>Upcoming</h3><p class="value">${upcomingCount}</p></div>
          <div class="overview-card"><h3>Overdue</h3><p class="value">${overdueCount}</p></div>
          <div class="overview-card"><h3>Completed (30d)</h3><p class="value">${this.completedCount}</p></div>
        </div>

        <div class="widget">
          <h3>PM Completion Rate</h3>
          <div style="display:flex; align-items:center; gap:0.75rem;">
            <div class="bar-chart"><div class="bar-fill" style="width: ${completionRate}%;"></div></div>
            <div style="min-width:48px; font-weight:600;">${completionRate}%</div>
          </div>
        </div>

        <!-- Analytics cards -->
        <div class="analytics-grid">
          <div class="metric-card">
            <div class="metric-title">PMs Linked to Work Orders</div>
            <div class="metric-value">${linked} / ${linkedTotal} ${allLinkedOk ? '✅' : '⚠️'}</div>
            <div class="metric-subtle">Live linkage check</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">On-Time Completion Rate</div>
            <div class="metric-value">${onTimePct}%</div>
            <div class="metric-subtle">Derived from completed PM WOs</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">Avg Completion Duration</div>
            <div class="metric-value">${avgDisplay}</div>
            <div class="metric-subtle">completed_at vs pm_due_at</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">PM Compliance Rate</div>
            <div class="metric-value">${this.pmAnalyticsSummary?.complianceRate ?? completionRate}%</div>
            <div class="metric-subtle">Completed / Total</div>
          </div>
        </div>
        ${integrityIssues > 0 ? html`<div class="metric-subtle">Data Integrity: ⚠️ ${integrityIssues} invalid refs</div>` : html`<div class="metric-subtle">Data Integrity: ✅ valid</div>`}

        <div class="widget">
          <h3>Upcoming PMs</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>Due Date</th><th>Assigned To</th><th>Status</th></tr></thead>
              <tbody>
                ${this.upcoming.map(pm => html`
                  <tr>
                    <td>${pm.asset}</td>
                    <td>${this.formatDate(pm.due)}</td>
                    <td>${this.users[pm.assigned_to] || pm.assigned_to || '-'}</td>
                    <td><sl-badge variant="primary">Upcoming</sl-badge></td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>

        <div class="widget">
          <h3>Overdue PMs</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>Due Date</th><th>Assigned To</th><th>Status</th></tr></thead>
              <tbody>
                ${this.overdue.map(pm => html`
                  <tr>
                    <td>${pm.asset}</td>
                    <td>${this.formatDate(pm.due)}</td>
                    <td>${this.users[pm.assigned_to] || pm.assigned_to || '-'}</td>
                    <td><sl-badge variant="danger">Overdue</sl-badge></td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-mini-dashboard': PMMiniDashboard;
  }
}
